import { X } from "lucide-react";
import { useEffect, useRef } from "react";

/**
 * SHEET — the modal contract, defined once.
 *
 * Bottom sheet on phone (rounded top corners, max-height --sheet-max-h, --elev-3), centred
 * dialog on desktop. Opaque. Body scroll locked. Focus moved to the heading on open. Tab
 * trapped inside. Escape AND backdrop click AND a visible 44px close button all dismiss.
 * Focus restored to the trigger on close.
 *
 * There is exactly one of these. Every overlay in the app — the walkthrough, the More menu,
 * the status chip, the prompt dock, the governance sheet — composes it rather than
 * re-improvising a backdrop, a z-index and a scroll lock.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input:not([type="hidden"]), select, summary, [tabindex]:not([tabindex="-1"])';

export function Sheet({
  title,
  eyebrow,
  onClose,
  children,
  footer,
  testId,
  labelledBy
}: {
  title: string;
  /** Optional kicker above the title. Purely additive: omit it and the header is unchanged. */
  eyebrow?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  testId?: string;
  labelledBy?: string;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const headingId = labelledBy || "sheet-title";

  /**
   * onClose through a ref, so the open-effect below can depend on NOTHING and run exactly
   * once — when the sheet opens. Callers pass an inline `onClose` (a fresh function every
   * render), and this effect moves focus to the heading. If it re-ran on every `onClose`
   * identity change it would re-focus the heading on every parent render — which is exactly
   * what happened when you typed into "Ask ATLAS": each keystroke re-rendered the editor,
   * gave the sheet a new onClose, re-ran this effect, and stole focus off the textarea after
   * one character. The ref keeps Escape calling the latest onClose without re-running the effect.
   */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    returnFocusRef.current = (document.activeElement as HTMLElement) || null;
    headingRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const card = cardRef.current;
      if (!card) return;
      const focusable = [...card.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (element) => element.offsetParent !== null
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;

      /**
       * RESTORE FOCUS TO THE TRIGGER — BUT ONLY IF NOBODY ELSE HAS CLAIMED IT.
       *
       * An unconditional restore is wrong, and it produced a real bug: the More sheet is the
       * ONLY way a phone reaches Pipeline, Settings and Help, and navigating from inside it
       * left focus sitting on the More TAB. The route-change effect focuses the new <h1>
       * first, React then unmounts this sheet, and this cleanup ran last and stole focus
       * straight back — so for exactly the three destinations that moved behind the sheet, a
       * keyboard or screen-reader operator was dumped back on the trigger and never landed on
       * the page they had just navigated to.
       *
       * So: restore only when focus is still OURS (inside the sheet — the Escape and close-
       * button paths) or ownerless (on <body> — the backdrop-click path, since clicking a div
       * focuses nothing). If some other element already holds focus, it took it deliberately
       * and it outranks us. The full modal contract is preserved; only the theft is removed.
       */
      const active = document.activeElement;
      const focusIsOurs = !active || active === document.body || cardRef.current?.contains(active);
      if (focusIsOurs) returnFocusRef.current?.focus?.();
    };
    // Run ONCE, when the sheet opens — not on every parent re-render. onClose is read live
    // from onCloseRef inside the handler above, so it never needs to be a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="sheet-backdrop"
      data-testid={testId ? `${testId}-backdrop` : undefined}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        data-testid={testId}
        ref={cardRef}
      >
        <div className="sheet-header">
          <div className="sheet-heading">
            {eyebrow ? <p className="sheet-eyebrow">{eyebrow}</p> : null}
            <h2 id={headingId} tabIndex={-1} ref={headingRef}>
              {title}
            </h2>
          </div>
          <button type="button" className="sheet-close" aria-label="Close" onClick={onClose}>
            <X aria-hidden="true" />
          </button>
        </div>
        <div className="sheet-body">{children}</div>
        {footer ? <div className="sheet-footer">{footer}</div> : null}
      </div>
    </div>
  );
}
